// src/components/DamageReport.js
import React, { useState } from 'react';

const COMMON_ISSUES = ['Missing towel', 'Stain on carpet', 'Broken lamp', 'Scratched table', 'Dirty mirror', 'Unmade bed'];

export default function DamageReport({ roomData, onReport }) {
  const [reports, setReports] = useState([]);
  const [currentIssue, setCurrentIssue] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Minor');
  const [photo, setPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const handleAddReport = () => {
    if (currentIssue || description) {
      const newReport = {
        id: Date.now(),
        issue: currentIssue || 'Custom issue',
        description,
        category: selectedCategory,
        photo,
        timestamp: new Date().toISOString()
      };
      setReports([...reports, newReport]);
      setCurrentIssue('');
      setDescription('');
      setPhoto(null);
      setPhotoFile(null);
    }
  };

  const handlePhotoUpload = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setPhotoFile(file);
      setPhoto(URL.createObjectURL(file));
    }
  };

  const handleRemoveReport = (id) => {
    setReports(reports.filter(report => report.id !== id));
  };

  const handleSubmit = () => {
    onReport(reports);
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'Minor': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Major': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'Damage': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'Minor': return '⚠️';
      case 'Major': return '🔧';
      case 'Damage': return '💔';
      default: return '📝';
    }
  };

  const getCategoryBg = (category) => {
    switch(category) {
      case 'Minor': return 'from-blue-500 to-blue-600';
      case 'Major': return 'from-indigo-500 to-indigo-600';
      case 'Damage': return 'from-red-500 to-red-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-white px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Damage & Missing Items Report
              </h1>
              <p className="text-gray-500 mt-1">Document any issues found during inspection</p>
            </div>
            <div className="bg-blue-50 rounded-xl px-4 py-2 border border-blue-200">
              <span className="text-blue-700 font-semibold">
                Room: {roomData?.roomId || 'Not allocated'}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Report Form */}
          <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-3 shadow-md">
                <span className="text-white text-xl">+</span>
              </div>
              <h2 className="text-xl font-bold text-gray-800">Add New Report</h2>
            </div>
            
            {/* Quick Select Issues */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Quick Select Common Issues
              </label>
              <div className="flex flex-wrap gap-2">
                {COMMON_ISSUES.map(issue => (
                  <button
                    key={issue}
                    onClick={() => setCurrentIssue(issue)}
                    className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-all transform hover:scale-105 border border-blue-200"
                  >
                    {issue}
                  </button>
                ))}
              </div>
            </div>

            {/* Issue Description */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Issue Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows="3"
                placeholder="Describe the damage or missing item in detail..."
              />
            </div>

            {/* Category Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Severity Category
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {['Minor', 'Major', 'Damage'].map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-xl font-semibold transition-all transform hover:scale-105 ${
                      selectedCategory === category
                        ? `${getCategoryColor(category)} ring-2 ring-offset-2 ring-blue-500`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="mr-1">{getCategoryIcon(category)}</span>
                    {category}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {selectedCategory === 'Minor' && '⚠️ Minor issues - Will be rechecked'}
                {selectedCategory === 'Major' && '🔧 Major issues - Requires immediate attention'}
                {selectedCategory === 'Damage' && '💔 Damage - May affect billing'}
              </p>
            </div>

            {/* Photo Upload */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Evidence Photo
              </label>
              <div className="border-2 border-dashed border-blue-200 rounded-xl p-6 text-center hover:border-blue-400 transition-colors bg-blue-50/30">
                {!photo ? (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="photo-upload"
                    />
                    <label htmlFor="photo-upload" className="cursor-pointer">
                      <div className="text-4xl mb-2">📸</div>
                      <p className="text-gray-600 mb-1">Click to upload photo</p>
                      <p className="text-xs text-gray-400">PNG, JPG up to 10MB</p>
                    </label>
                  </>
                ) : (
                  <div className="relative inline-block">
                    <img src={photo} alt="Evidence" className="w-32 h-32 object-cover rounded-lg border-2 border-blue-300 shadow-md" />
                    <button
                      onClick={() => {
                        setPhoto(null);
                        setPhotoFile(null);
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Add Report Button */}
            <button
              onClick={handleAddReport}
              disabled={!currentIssue && !description}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              + Add Report
            </button>
          </div>

          {/* Right: Reports List */}
          <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-6 flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-3 shadow-md">
                  <span className="text-white text-xl">📋</span>
                </div>
                <h2 className="text-xl font-bold text-gray-800">
                  Reported Issues
                  {reports.length > 0 && (
                    <span className="ml-2 text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      {reports.length}
                    </span>
                  )}
                </h2>
              </div>
              {reports.length > 0 && (
                <button
                  onClick={handleSubmit}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105 text-sm"
                >
                  Submit {reports.length} Report{reports.length !== 1 && 's'} →
                </button>
              )}
            </div>
            
            {reports.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-gray-400 font-medium">No issues reported yet</p>
                <p className="text-gray-400 text-sm mt-1">Add damage or missing items from the left panel</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                {reports.map((report, index) => (
                  <div
                    key={report.id}
                    className="border border-blue-100 rounded-xl p-4 bg-white hover:shadow-md transition-all group"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getCategoryColor(report.category)}`}>
                            {getCategoryIcon(report.category)} {report.category}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(report.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="font-semibold text-gray-800 mb-1">{report.issue}</p>
                        {report.description && (
                          <p className="text-sm text-gray-600">{report.description}</p>
                        )}
                      </div>
                      <div className="flex items-start gap-2">
                        {report.photo && (
                          <img
                            src={report.photo}
                            alt="evidence"
                            className="w-16 h-16 object-cover rounded-lg border border-blue-200"
                          />
                        )}
                        <button
                          onClick={() => handleRemoveReport(report.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary Footer */}
            {reports.length > 0 && (
              <div className="mt-4 pt-4 border-t border-blue-100">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Issues:</span>
                  <span className="font-semibold text-blue-700">{reports.length}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">By Category:</span>
                  <div className="flex gap-3">
                    {reports.filter(r => r.category === 'Minor').length > 0 && (
                      <span className="text-blue-600">Minor: {reports.filter(r => r.category === 'Minor').length}</span>
                     )}
                    {reports.filter(r => r.category === 'Major').length > 0 && (
                      <span className="text-indigo-600">Major: {reports.filter(r => r.category === 'Major').length}</span>
                    )}
                    {reports.filter(r => r.category === 'Damage').length > 0 && (
                      <span className="text-red-600">Damage: {reports.filter(r => r.category === 'Damage').length}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}